import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  downloadSurveyCsv,
  getSurveyAggregation,
  getSurveyQuestionAnswers,
  type TSurveyAggregation,
} from 'api/surveys'
import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { pageCardClasses } from 'components/AppLayout/styles'
import { routes } from 'constants/routes'
import { useOrgPath } from 'hooks/useOrgPath'
import { getSurveyErrorMessage } from './errors'

export default function SurveyAggregationPage() {
  const navigate = useNavigate()
  const orgPathFor = useOrgPath()
  const { surveyId } = useParams<{ surveyId: string }>()
  const loadSequence = useRef(0)
  const csvInFlight = useRef(false)
  const answerPageInFlight = useRef(false)
  const [aggregation, setAggregation] = useState<TSurveyAggregation | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [csvError, setCsvError] = useState<string | null>(null)
  const [csvDownloading, setCsvDownloading] = useState(false)
  const [loadingQuestionId, setLoadingQuestionId] = useState<number | null>(null)
  const [answerLoadError, setAnswerLoadError] = useState<string | null>(null)

  const numericSurveyId = Number(surveyId)
  const hasValidSurveyId =
    Number.isInteger(numericSurveyId) && numericSurveyId > 0

  const loadAggregation = useCallback(async () => {
    if (!hasValidSurveyId) {
      setLoading(false)
      setLoadError('アンケートIDが正しくありません。')
      return
    }

    const requestSequence = ++loadSequence.current
    setLoading(true)
    setLoadError(null)
    setCsvError(null)
    setAggregation(null)
    try {
      const result = await getSurveyAggregation(numericSurveyId)
      if (requestSequence !== loadSequence.current) return
      setAggregation(result)
    } catch (error: unknown) {
      if (requestSequence !== loadSequence.current) return
      setAggregation(null)
      setLoadError(
        getSurveyErrorMessage(error, '集計の読み込みに失敗しました。'),
      )
    } finally {
      if (requestSequence === loadSequence.current) {
        setLoading(false)
      }
    }
  }, [hasValidSurveyId, numericSurveyId])

  useEffect(() => {
    void loadAggregation()
    return () => {
      loadSequence.current += 1
    }
  }, [loadAggregation])

  const handleDownload = async () => {
    if (!hasValidSurveyId || csvInFlight.current) return
    csvInFlight.current = true
    setCsvDownloading(true)
    setCsvError(null)
    try {
      const blob = await downloadSurveyCsv(numericSurveyId)
      const url = URL.createObjectURL(blob)
      try {
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = `survey-${numericSurveyId}.csv`
        anchor.click()
      } finally {
        URL.revokeObjectURL(url)
      }
    } catch (error: unknown) {
      setCsvError(
        getSurveyErrorMessage(error, 'CSVのダウンロードに失敗しました。'),
      )
    } finally {
      csvInFlight.current = false
      setCsvDownloading(false)
    }
  }

  const handleLoadMore = async (
    questionId: number,
    cursor: number,
    answerWatermark: number,
  ) => {
    if (
      !hasValidSurveyId ||
      loadingQuestionId !== null ||
      answerPageInFlight.current
    ) {
      return
    }
    answerPageInFlight.current = true
    setLoadingQuestionId(questionId)
    setAnswerLoadError(null)
    try {
      const page = await getSurveyQuestionAnswers(
        numericSurveyId,
        questionId,
        cursor,
        answerWatermark,
      )
      setAggregation((current) => {
        if (!current) return current
        return {
          ...current,
          questions: current.questions.map((question) =>
            question.questionId === questionId
              ? {
                  ...question,
                  answers: [...(question.answers ?? []), ...page.answers],
                  nextCursor: page.nextCursor,
                }
              : question,
          ),
        }
      })
    } catch (error: unknown) {
      setAnswerLoadError(
        getSurveyErrorMessage(error, '回答の読み込みに失敗しました。'),
      )
    } finally {
      answerPageInFlight.current = false
      setLoadingQuestionId(null)
    }
  }

  const stats = aggregation
    ? [
        { label: '配信対象', value: `${aggregation.targetCount}人` },
        { label: '回答開始', value: `${aggregation.startedCount}件` },
        { label: '回答完了', value: `${aggregation.completedCount}件` },
        {
          label: '回答率',
          value: `${aggregation.completionRate}%`,
        },
      ]
    : []

  return (
    <>
      <section className={`w-full space-y-5 ${pageCardClasses.large}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-2">
            <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-muted">
              survey aggregation
            </span>
            <h1 className="text-2xl font-display text-ink">アンケート集計</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              disabled={
                !aggregation || loading || csvDownloading || !hasValidSurveyId
              }
              onClick={() => void handleDownload()}
            >
              {csvDownloading ? 'ダウンロード中...' : 'CSVダウンロード'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => navigate(orgPathFor(routes.SURVEY_MANAGEMENT))}
            >
              戻る
            </Button>
          </div>
        </header>

        {loadError ? (
          <div className="space-y-3">
            <Alert>{loadError}</Alert>
            {hasValidSurveyId ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={loading}
                onClick={() => void loadAggregation()}
              >
                再読み込み
              </Button>
            ) : null}
          </div>
        ) : null}
        {csvError ? <Alert>{csvError}</Alert> : null}
        {answerLoadError ? <Alert>{answerLoadError}</Alert> : null}
        {loading ? <p role="status">読み込み中...</p> : null}

        {aggregation ? (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="grid gap-1 rounded-2xl border border-border/70 bg-white p-4 shadow-sm"
                >
                  <span className="text-xs font-semibold text-muted">
                    {stat.label}
                  </span>
                  <span className="text-xl font-semibold text-ink">
                    {stat.value}
                  </span>
                </div>
              ))}
            </div>

            {aggregation.questions.map((question, questionIndex) => (
              <div
                key={question.questionId}
                className="space-y-3 rounded-2xl border border-border/70 bg-white p-5 shadow-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-white">
                      {questionIndex + 1}
                    </span>
                    <h2 className="text-sm font-semibold text-ink">
                      {question.title}
                    </h2>
                  </div>
                  <p className="text-xs text-muted">
                    回答数: {question.answeredCount}件
                  </p>
                </div>
                {question.choiceCounts ? (
                  <ul className="space-y-2">
                    {question.choiceCounts.map((choice) => (
                      <li key={choice.choiceId} className="grid gap-1">
                        <div className="flex items-center justify-between gap-3 text-sm text-ink">
                          <span>{choice.label}</span>
                          <span className="shrink-0 text-xs text-muted">
                            {choice.count}件 ({choice.percentage}%)
                          </span>
                        </div>
                        <div
                          className="h-2 w-full overflow-hidden rounded-full bg-surface"
                          aria-hidden="true"
                        >
                          <div
                            className="h-full rounded-full bg-accent"
                            style={{
                              width: `${Math.min(100, Math.max(0, choice.percentage))}%`,
                            }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (question.answers?.length ?? 0) > 0 ? (
                  <ul className="space-y-2">
                    {question.answers?.map((answer) => (
                      <li
                        key={answer.id}
                        className="rounded-xl bg-surface/80 px-4 py-2.5 text-sm text-ink"
                      >
                        {answer.value}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted">回答はまだありません。</p>
                )}
                {question.nextCursor && question.answerWatermark ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={loadingQuestionId !== null}
                    onClick={() =>
                      void handleLoadMore(
                        question.questionId,
                        question.nextCursor!,
                        question.answerWatermark!,
                      )
                    }
                  >
                    {loadingQuestionId === question.questionId
                      ? '読み込み中...'
                      : 'さらに読み込む'}
                  </Button>
                ) : null}
              </div>
            ))}
          </>
        ) : null}
      </section>
    </>
  )
}
